import React, { useState } from 'react';
import { getGiftCardBalance } from '../services/api';
import { useI18n } from '../i18n/i18nContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { GiftCard } from '@shared/types';
import SEO from '../components/SEO';
import { formatPrice } from '@shared/utils';

export const GiftCardBalancePage: React.FC = () => {
  const { t, language } = useI18n();
  useDocumentTitle(t.pageTitles.checkBalance);
  const [code, setCode] = useState('');
  const [giftCard, setGiftCard] = useState<GiftCard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const formatGiftCardCode = (value: string) => {
    // Remove all non-alphanumeric characters
    const cleaned = value.replace(/[^A-Z0-9]/g, '');
    
    // Add dashes after every 4 characters
    const parts = [];
    for (let i = 0; i < cleaned.length; i += 4) {
      parts.push(cleaned.slice(i, i + 4));
    }
    
    // Join with dashes and limit to 3 groups (12 characters)
    return parts.slice(0, 3).join('-');
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatGiftCardCode(e.target.value.toUpperCase());
    setCode(formatted);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setGiftCard(null);

    // Check if the input is empty
    if (!code.trim()) {
      setError(t.giftCard.enterCode);
      return;
    }

    setLoading(true);

    try {
      const response = await getGiftCardBalance(code.trim());
      setGiftCard(response.data as any);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError(t.giftCard.invalidCode);
      } else {
        setError(t.giftCard.checkError);
      }
      console.error(t.console.errors.fetchingGiftCardBalance, err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-8 text-center">
      <SEO description={t.seo.checkBalanceDescription} />
      <h1 className="text-3xl font-bold mb-6">{t.giftCard.balancePageTitle}</h1>
      <p className="mb-8 text-gray-600">{t.giftCard.balancePageSubtitle}</p>
      
      <form onSubmit={handleSubmit} className="max-w-md mx-auto">
        <div className="flex items-center border-b-2 border-teal-500 py-2 mb-4">
          <input
            className="appearance-none bg-transparent border-none w-full text-gray-700 mr-3 py-1 px-2 leading-tight focus:outline-none"
            type="text"
            placeholder={t.giftCard.codePlaceholder}
            value={code}
            onChange={handleCodeChange}
            maxLength={14}
            disabled={loading}
          />
          <button
            className="flex-shrink-0 bg-teal-500 hover:bg-teal-700 border-teal-500 hover:border-teal-700 text-sm border-4 text-white py-1 px-2 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            type="submit"
            disabled={loading}
          >
            {loading ? t.giftCard.checkingBalance : t.giftCard.checkBalance}
          </button>
        </div>
      </form>

      {error && (
        <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded max-w-md mx-auto">
          <p>{error}</p>
        </div>
      )}
      
      {giftCard && (
        <div className={`mt-6 p-6 rounded-lg max-w-md mx-auto ${giftCard.active ? 'bg-green-100' : 'bg-red-100'}`}>
          <p className="text-lg font-semibold text-gray-700 mb-2">{giftCard.active ? t.giftCard.currentBalance : t.giftCard.cardDepleted}</p>
          <p className={`text-3xl font-bold ${giftCard.active ? 'text-green-600' : 'text-red-700'}`}>{formatPrice(parseFloat(String(giftCard.balance)))}</p>
          
          {giftCard.initial_balance && (
            <p className="text-sm text-gray-600 mt-2">
              Initial balance: {formatPrice(parseFloat(String(giftCard.initial_balance)))}
            </p>
          )}
          
          {giftCard.recipient_name && (
            <div className={`mt-4 pt-4 ${giftCard.active ? 'border-t border-green-200' : 'border-t border-red-200'}`}>
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Recipient:</span> {giftCard.recipient_name}
              </p>
            </div>
          )}
          
          {giftCard.message && (
            <div className="mt-2">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Message:</span> {giftCard.message}
              </p>
            </div>
          )}
          
          {giftCard.redemption_history && giftCard.redemption_history.length > 0 && (
            <div className={`mt-4 pt-4 ${giftCard.active ? 'border-t border-green-200' : 'border-t border-red-200'}`}>
              <p className="text-sm font-semibold text-gray-700 mb-2">Redemption History:</p>
              <div className="space-y-2">
                {giftCard.redemption_history.map((item, index) => (
                  <div key={index} className="text-sm text-gray-600 bg-white bg-opacity-50 p-2 rounded">
                    <div className="flex justify-between">
                      <span>{new Date(item.date).toLocaleDateString(language, { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                      <span className="font-semibold">-{formatPrice(parseFloat(String(item.amount)))}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Balance after: {formatPrice(parseFloat(String(item.remaining_balance)))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};